/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { ClinicalDepartmentEnum } from '../models/clinical-department-enum';
import { FormPriorFieldContextResponse } from '../models/form-prior-field-context-response';

/**
 * Latest prior field context for a selected filter value (e.g. base). Driven by metadata isBaseContextFilterKey / includeInBaseContext; returns raw field values plus source date.
 */
@Injectable({ providedIn: 'root' })
export class FormPriorFieldContextService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getFormPriorFieldContext()` */
  static readonly GetFormPriorFieldContextPath =
    '/api/v1/clinical/{department}/forms/{formType}/prior-field-context';

  /**
   * Latest prior field context for a selected base (or other filter key).
   *
   * Finds the latest SUBMITTED/REVIEWING/APPROVED submission for this form type matching the configured context filter (metadata `isBaseContextFilterKey`, e.g. base) and returns raw values for fields marked `includeInBaseContext=true`, plus source date (`date` as dd/MM/yyyy and `rawDate` as ISO date). Reusable across form types via metadata; currently configured for HEMS_BASE_DUTIES_DAILY. Requires the form-type CREATE privilege.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getFormPriorFieldContext()` instead.
   *
   * This method doesn't expect any request body.
   */
  getFormPriorFieldContext$Response(
    params: {
      department: ClinicalDepartmentEnum;
      formType: string;
      contextValue: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<FormPriorFieldContextResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FormPriorFieldContextService.GetFormPriorFieldContextPath,
      'get'
    );
    if (params) {
      rb.path('department', params.department, {});
      rb.path('formType', params.formType, {});
      rb.query('contextValue', params.contextValue, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<FormPriorFieldContextResponse>;
        })
      );
  }

  /**
   * Latest prior field context for a selected base (or other filter key).
   *
   * Finds the latest SUBMITTED/REVIEWING/APPROVED submission for this form type matching the configured context filter (metadata `isBaseContextFilterKey`, e.g. base) and returns raw values for fields marked `includeInBaseContext=true`, plus source date (`date` as dd/MM/yyyy and `rawDate` as ISO date). Reusable across form types via metadata; currently configured for HEMS_BASE_DUTIES_DAILY. Requires the form-type CREATE privilege.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getFormPriorFieldContext$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getFormPriorFieldContext(
    params: {
      department: ClinicalDepartmentEnum;
      formType: string;
      contextValue: string;
    },
    context?: HttpContext
  ): Observable<FormPriorFieldContextResponse> {
    return this.getFormPriorFieldContext$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<FormPriorFieldContextResponse>
        ): FormPriorFieldContextResponse => r.body
      )
    );
  }
}
